import { useEffect, useRef, useState } from 'react';

import { type PlacementHelpAxis } from '../talentReviewPlacementHelp';

export type TalentReviewPlacementHelpStep = {
	id: string;
	axis: PlacementHelpAxis;
	axisLabel: string;
	question: string;
	answers: { id: string; label: string; tickIndex?: number | null }[];
};

export default function useTalentReviewPlacement(steps: TalentReviewPlacementHelpStep[]) {
	const stepCount = steps.length + 1;
	const [stepIndex, setStepIndex] = useState(0);
	const [answers, setAnswers] = useState<(string | undefined)[]>(() => steps.map(() => undefined));
	const isResult = stepIndex === steps.length;
	const advanceTimeout = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () => clearTimeout(advanceTimeout.current), []);

	const handleSelect = (index: number, answerID: string) => {
		setAnswers((current) => current.map((value, item) => (item === index ? answerID : value)));
		clearTimeout(advanceTimeout.current);
		advanceTimeout.current = setTimeout(() => {
			setStepIndex((current) => (current === index ? Math.min(stepCount - 1, index + 1) : current));
		}, 200);
	};

	const handlePrevious = () => {
		setStepIndex((index) => Math.max(0, index - 1));
	};

	const canGoNext = isResult || !!answers[stepIndex];

	const canGoTo = (index: number) =>
		index >= 0 && index < stepCount && answers.slice(0, index).every((answer) => !!answer);

	const handleGoTo = (index: number) => {
		if (!canGoTo(index)) {
			return;
		}

		setStepIndex(index);
	};

	const handleNext = () => {
		if (!canGoNext) {
			return;
		}

		setStepIndex((index) => Math.min(stepCount - 1, index + 1));
	};

	return {
		stepIndex,
		stepCount,
		questions: steps.map((step, index) => ({
			...step,
			selected: answers[index],
		})),
		isFirst: stepIndex === 0,
		isLast: stepIndex === stepCount - 1,
		isResult,
		canGoNext,
		canGoTo,
		handleGoTo,
		handleSelect,
		handlePrevious,
		handleNext,
	};
}
